import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { Dialog, DialogContent } from '@rocco/components/dialog';
import { TextRotate } from '@rocco/components/text-rotate';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as APLogoWhite } from '@rocco/icons/svg/logo-new-white.svg';
import { getMatchedModalOpenAtom } from '~/state/ui-state-get-matched-modal';
import { useGetMatchedSubmission } from '~/modules/get-matched/hooks/use-get-matched-submission';
import { useBuildHubProjectsForMatch } from '~/modules/get-matched/hooks/use-buildhub-projects-for-match';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import {
    findBestMatch,
    budgetBucketFromRange,
    normalizeScope,
    type MatchScore,
} from '~/modules/get-matched/utils/match-buildhub-project';
import { GetMatchedIntroModal } from './GetMatchedIntroModal';
import { Step0EnquiryType } from './Step0EnquiryType';
import { Step1UserType, type UserType } from './Step1UserType';
import { Step2Location } from './Step2Location';
import { Step3ProjectType } from './Step3ProjectType';
import { Step4ProjectScope } from './Step4ProjectScope';
import {
    Step4ProductCategory,
    type ProductCategoryOption,
} from './Step4ProductCategory';
import { Step4Style } from './Step4Style';
import { Step5Budget } from './Step5Budget';
import { Step6Project } from './Step6Project';
import { Step7Account } from './Step7Account';
import { Step8Done } from './Step8Done';
import { StepMatchProject } from './StepMatchProject';
import {
    ENQUIRY_CONFIG,
    SELECTOR_LEFT_PANEL,
    type EnquiryType,
    type EnquiryVariantConfig,
    type StepKey,
} from './enquiryConfig';

/**
 * Build the step list for the active enquiry type. Prepends a type-selector
 * step when no `defaultEnquiryType` is set. The `matchProject` step rides on
 * `projectScope`; flows without it (product, project) skip the BuildHub match.
 */
const buildStepFlow = ({
    enquiryType,
    defaultEnquiryType,
    matched,
}: {
    enquiryType: EnquiryType | null;
    defaultEnquiryType: EnquiryType | undefined;
    matched: MatchScore | null;
}): StepKey[] => {
    const baseFlow = enquiryType
        ? ENQUIRY_CONFIG[enquiryType].flow
        : ENQUIRY_CONFIG.professional.flow;

    const withMatch =
        matched && baseFlow.includes('projectScope')
            ? insertAfter(baseFlow, 'projectScope', 'matchProject')
            : baseFlow;

    return defaultEnquiryType ? withMatch : ['enquiryType', ...withMatch];
};

const insertAfter = (
    flow: StepKey[],
    anchor: StepKey,
    insert: StepKey,
): StepKey[] => {
    const idx = flow.indexOf(anchor);
    if (idx < 0) return flow;
    return [...flow.slice(0, idx + 1), insert, ...flow.slice(idx + 1)];
};

// BuildHub serialises Datetime as `YYYY-MM-DD HH:MM:SS`; <input type="date">
// only renders a `YYYY-MM-DD` value.
const toDateInputValue = (raw: string | null): string | null => {
    if (!raw) return null;
    const date = raw.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
};

const BRIEF_STORAGE_KEY = 'getMatched.pendingBrief';
const BRIEF_STORAGE_TTL_MS = 30 * 60 * 1000;
const ATTACHMENT_RESUME_NOTICE =
    "Files don't carry across sign-in — please re-attach to continue.";

interface PersistedBrief {
    answers: Omit<MatchAnswers, 'attachments'>;
    hasAttachments: boolean;
    savedAt: number;
}

const persistBrief = (answers: MatchAnswers) => {
    if (typeof window === 'undefined') return;
    const { attachments, ...rest } = answers;
    const payload: PersistedBrief = {
        answers: rest,
        hasAttachments: attachments.length > 0,
        savedAt: Date.now(),
    };
    try {
        window.sessionStorage.setItem(
            BRIEF_STORAGE_KEY,
            JSON.stringify(payload),
        );
    } catch {
        // sessionStorage unavailable / quota — silent. The user can still
        // complete the OAuth flow; they'll just lose the in-progress brief.
    }
};

const consumePersistedBrief = (): PersistedBrief | null => {
    if (typeof window === 'undefined') return null;
    let raw: string | null = null;
    try {
        raw = window.sessionStorage.getItem(BRIEF_STORAGE_KEY);
    } catch {
        return null;
    }
    if (!raw) return null;
    try {
        window.sessionStorage.removeItem(BRIEF_STORAGE_KEY);
    } catch {
        // best-effort cleanup
    }
    try {
        const parsed = JSON.parse(raw) as PersistedBrief;
        if (!parsed?.savedAt || !parsed.answers) return null;
        if (Date.now() - parsed.savedAt > BRIEF_STORAGE_TTL_MS) return null;
        return parsed;
    } catch {
        return null;
    }
};

interface MatchAnswers {
    enquiryType: EnquiryType | null;
    userType: UserType | null;
    region: string | null;
    projectType: string | null;
    projectScope: string | null;
    /**
     * Stores the answer to the product-flow "what kind of product" question.
     * Submitted via the same `ProfessionalType` DB column on the backend —
     * the column holds "what the user is looking for" regardless of variant.
     */
    productCategory: string | null;
    style: string | null;
    budget: string | null;
    description: string;
    professionals: string | null;
    startDate: string | null;
    endDate: string | null;
    email: string;
    attachments: File[];
    buildHubID: number | null;
}

const buildInitialAnswers = (
    defaultEnquiryType: EnquiryType | undefined,
): MatchAnswers => ({
    enquiryType: defaultEnquiryType ?? null,
    userType: null,
    region: null,
    projectType: null,
    projectScope: null,
    productCategory: null,
    style: null,
    budget: null,
    description: '',
    professionals: null,
    startDate: null,
    endDate: null,
    email: '',
    attachments: [],
    buildHubID: null,
});

interface StyleImage {
    style: string;
    imageUrl: string | null;
}

interface GetMatchedModalProps {
    backgroundImage: string | null;
    /** Keyed by style title; rows without an uploaded image fall back to the grey placeholder tile. */
    styleImages?: StyleImage[];
    /**
     * Fixes the enquiry type at mount time. When omitted the modal opens with
     * a type-selector step (used on pages where the audience isn't known —
     * homepage, /app etc).
     */
    defaultEnquiryType?: EnquiryType;
    /**
     * Top-level product categories surfaced into the productCategory step of
     * the product flow. Pass from the consumer (e.g. /products route loader);
     * falls back to a small static list from `enquiryConfig.ts` when omitted.
     */
    productCategories?: ProductCategoryOption[];
}

export const GetMatchedModal = ({
    backgroundImage,
    styleImages,
    defaultEnquiryType,
    productCategories,
}: GetMatchedModalProps) => {
    const [open, setOpen] = useAtom(getMatchedModalOpenAtom);
    const [step, setStep] = useState(1);
    const [answers, setAnswers] = useState<MatchAnswers>(() =>
        buildInitialAnswers(defaultEnquiryType),
    );
    const [attachmentNotice, setAttachmentNotice] = useState<string | null>(
        null,
    );
    const [justRegistered, setJustRegistered] = useState(false);
    const [matchedProject, setMatchedProject] = useState<MatchScore | null>(
        null,
    );
    const [pendingMatchAnswers, setPendingMatchAnswers] =
        useState<MatchAnswers | null>(null);
    const submission = useGetMatchedSubmission();
    const userLogin = useUserLoggedIn();
    const accountEmail = userLogin.isLoggedIn
        ? (userLogin.user.Email ?? null)
        : null;
    const pendingAutoSubmitRef = useRef(false);

    const flow = buildStepFlow({
        enquiryType: answers.enquiryType,
        defaultEnquiryType,
        matched: matchedProject,
    });
    const totalSteps = flow.length;
    const currentStepKey = flow[step - 1] ?? flow[0] ?? 'userType';
    // Fallback to the professional variant for child copy when the user
    // hasn't picked a type yet — keeps step components mountable without
    // null checks. The LeftPanel ignores this fallback during the selector
    // step and uses SELECTOR_LEFT_PANEL instead.
    const activeEnquiryType: EnquiryType =
        answers.enquiryType ?? defaultEnquiryType ?? 'professional';
    const variantConfig = ENQUIRY_CONFIG[activeEnquiryType];
    const leftPanelCopy =
        currentStepKey === 'enquiryType'
            ? SELECTOR_LEFT_PANEL
            : variantConfig.left;

    // Apollo isn't mounted on category pages, so the matcher's project list
    // comes through a remix-api loader via useFetcher.
    const projectsForMatch = useBuildHubProjectsForMatch();
    const { fetchProjects } = projectsForMatch;

    useEffect(() => {
        if (open && userLogin.isLoggedIn) {
            fetchProjects();
        }
    }, [open, userLogin.isLoggedIn, fetchProjects]);

    useEffect(() => {
        if (!open) {
            setStep(1);
            setAnswers(buildInitialAnswers(defaultEnquiryType));
            setAttachmentNotice(null);
            setJustRegistered(false);
            setMatchedProject(null);
            setPendingMatchAnswers(null);
            pendingAutoSubmitRef.current = false;
            submission.reset();
        }
    }, [open, defaultEnquiryType]);

    // Restore a brief stashed in sessionStorage before social-auth redirected
    // away. Wait for `isLoggedIn` to hydrate so we don't discard it on the
    // unsettled render. One-shot via the ref.
    const oauthResumeConsumedRef = useRef(false);
    useEffect(() => {
        if (oauthResumeConsumedRef.current) return;
        if (!userLogin.isLoggedIn) return;
        oauthResumeConsumedRef.current = true;

        const persisted = consumePersistedBrief();
        if (!persisted) return;

        const restored: MatchAnswers = {
            ...buildInitialAnswers(defaultEnquiryType),
            ...persisted.answers,
            email: userLogin.user.Email ?? persisted.answers.email ?? '',
            attachments: [],
        };
        setAnswers(restored);
        setOpen(true);

        // Compute the resume step against the restored enquiry type's flow —
        // a product-flow brief has no scope/style and a different `details`
        // index than a professional brief.
        const resumeFlow = buildStepFlow({
            enquiryType: restored.enquiryType,
            defaultEnquiryType,
            matched: null,
        });
        const stepIndex = (key: StepKey) => {
            const idx = resumeFlow.indexOf(key);
            return idx >= 0 ? idx + 1 : 1;
        };

        if (persisted.hasAttachments) {
            // Files can't survive a redirect — bounce back so the user can re-attach.
            setStep(stepIndex('details'));
            setAttachmentNotice(ATTACHMENT_RESUME_NOTICE);
        } else {
            setStep(stepIndex('account'));
            pendingAutoSubmitRef.current = true;
        }
    }, [userLogin.isLoggedIn, setOpen, defaultEnquiryType]);

    // Fire the auto-submit one tick after restoration so the submit hook
    // observes the restored answers.
    useEffect(() => {
        if (!pendingAutoSubmitRef.current) return;
        if (currentStepKey !== 'account') return;
        pendingAutoSubmitRef.current = false;
        handleSubmitInternal(answers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStepKey]);

    useEffect(() => {
        if (submission.status === 'success') {
            setStep(totalSteps);
        }
    }, [submission.status, totalSteps]);

    // Warm the browser cache for the style-step photography so it's decoded
    // before the user reaches that step (otherwise images flash on first paint).
    useEffect(() => {
        if (!open || !styleImages?.length || typeof window === 'undefined') {
            return;
        }
        for (const row of styleImages) {
            if (row.imageUrl) {
                const img = new window.Image();
                img.src = row.imageUrl;
            }
        }
    }, [open, styleImages]);

    const styleImageMap = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        for (const row of styleImages ?? []) {
            if (row.imageUrl) {
                map[row.style] = row.imageUrl;
            }
        }
        return map;
    }, [styleImages]);

    const isSubmitting = submission.status === 'submitting';

    const close = () => {
        if (isSubmitting) return;
        setOpen(false);
    };

    const runMatch = (next: MatchAnswers): MatchScore | null =>
        findBestMatch(
            {
                region: next.region,
                projectScope: next.projectScope,
                budget: next.budget,
                startDate: next.startDate,
                endDate: next.endDate,
            },
            projectsForMatch.projects,
        );

    // Callers that have just-computed values for the flow inputs (e.g. fresh
    // enquiryType after a selector pick, or a fresh matchedProject) pass the
    // resolved flow in. Default branch falls back to render-time closure values
    // — fine for back-button presses where nothing in-flight has changed.
    const goNext = (overrideFlow?: StepKey[]) =>
        setStep((s) => {
            const nextFlow =
                overrideFlow ??
                buildStepFlow({
                    enquiryType: answers.enquiryType,
                    defaultEnquiryType,
                    matched: matchedProject,
                });
            return Math.min(s + 1, nextFlow.length);
        });
    const goBack = () => {
        if (isSubmitting) return;
        setStep((s) => Math.max(s - 1, 1));
    };

    // Steps 1-4 auto-advance on click, so a fast click-through reaches Step 4
    // before the BuildHub fetch returns. Defer the advance when projects
    // aren't ready yet so the user doesn't land on `style` and then jump back
    // to `matchProject` mid-render.
    const goNextFromCurrent = (latestAnswers: MatchAnswers) => {
        let nextMatched = matchedProject;
        if (currentStepKey === 'projectScope' && userLogin.isLoggedIn) {
            if (
                projectsForMatch.projects.length === 0 &&
                projectsForMatch.loading
            ) {
                setPendingMatchAnswers(latestAnswers);
                return;
            }
            nextMatched = runMatch(latestAnswers);
            setMatchedProject(nextMatched);
        }
        goNext(
            buildStepFlow({
                enquiryType: latestAnswers.enquiryType,
                defaultEnquiryType,
                matched: nextMatched,
            }),
        );
    };

    useEffect(() => {
        if (!pendingMatchAnswers) return;
        if (projectsForMatch.loading) return;
        const next = pendingMatchAnswers;
        setPendingMatchAnswers(null);
        const nextMatched = runMatch(next);
        setMatchedProject(nextMatched);
        goNext(
            buildStepFlow({
                enquiryType: next.enquiryType,
                defaultEnquiryType,
                matched: nextMatched,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingMatchAnswers, projectsForMatch.loading]);

    // Cap the wait so a stalled fetch never hangs the modal — give up and
    // advance without a match. Drop any stale suggestion and rebuild the flow
    // with `matched: null` so the `matchProject` step isn't shown.
    useEffect(() => {
        if (!pendingMatchAnswers) return;
        const next = pendingMatchAnswers;
        const timeout = window.setTimeout(() => {
            setPendingMatchAnswers(null);
            setMatchedProject(null);
            goNext(
                buildStepFlow({
                    enquiryType: next.enquiryType,
                    defaultEnquiryType,
                    matched: null,
                }),
            );
        }, 1500);
        return () => window.clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingMatchAnswers]);

    const updateAnswers = (patch: Partial<MatchAnswers>) =>
        setAnswers((prev) => ({ ...prev, ...patch }));

    const handleAcceptMatch = () => {
        if (!matchedProject) return;
        const project = matchedProject.project;
        setAnswers((prev) => {
            const inferredBudget =
                prev.budget == null
                    ? budgetBucketFromRange(project.Budget)
                    : prev.budget;
            const inferredRegion =
                prev.region == null || prev.region === ''
                    ? (project.LocationData?.City ??
                      project.LocationData?.Suburb ??
                      project.Location ??
                      null)
                    : prev.region;
            return {
                ...prev,
                region: inferredRegion,
                projectScope:
                    normalizeScope(project.ProjectScope) ?? prev.projectScope,
                budget: inferredBudget,
                description: project.MainDescription ?? prev.description,
                startDate:
                    toDateInputValue(project.StartDate) ?? prev.startDate,
                endDate: toDateInputValue(project.EndDate) ?? prev.endDate,
                buildHubID: project.ID,
            };
        });
        goNext();
    };

    const handleSkipMatch = () => {
        // User declined the suggestion — clear the link, keep their answers, advance.
        setAnswers((prev) => ({ ...prev, buildHubID: null }));
        goNext();
    };

    const handleSubmitInternal = (source: MatchAnswers) => {
        if (isSubmitting) return;
        const effectiveEmail = accountEmail ?? source.email;
        const effectiveEnquiryType =
            source.enquiryType ?? defaultEnquiryType ?? null;
        submission.submit({
            enquiryType: effectiveEnquiryType,
            userType: source.userType,
            region: source.region,
            projectType: source.projectType,
            projectScope: source.projectScope,
            style: source.style,
            budget: source.budget,
            description: source.description,
            // Product flow stores the chosen category in the same DB column
            // (`ProfessionalType`) that the other variants use for their
            // "what are you looking for" answer.
            professionalType:
                effectiveEnquiryType === 'product'
                    ? source.productCategory
                    : source.professionals,
            startDate: source.startDate,
            endDate: source.endDate,
            email: effectiveEmail,
            attachments: source.attachments,
            buildHubID: source.buildHubID,
        });
    };

    const handleSubmit = () => handleSubmitInternal(answers);

    const handlePersistBeforeAuth = () => persistBrief(answers);

    return (
        <>
            {/* Triggers open the intro card, not the brief — mounted here so
                every consumer of this component gets the interim step without
                wiring it up itself. */}
            <GetMatchedIntroModal />
            <Dialog
                open={open}
                onOpenChange={(next) => {
                    if (isSubmitting) return;
                    setOpen(next);
                }}
            >
                <DialogContent
                    spacing="none"
                    // Below `md` the brief is a full-screen single-column
                    // sheet: the left panel is decorative, so only the form
                    // column shows. `md:` restores the two-panel dialog.
                    // `sm:rounded-none` defeats the shared variant's
                    // `sm:rounded-lg` on the full-bleed sheet; `md:rounded-lg`
                    // keeps the desktop radius that variant already applied.
                    className="grid h-dvh max-h-dvh w-full max-w-full grid-cols-1 grid-rows-[minmax(0,1fr)] gap-0 overflow-hidden rounded-none border-0 bg-transparent p-0 shadow-2xl sm:rounded-none md:h-[56.25rem] md:max-h-[90vh] md:w-[59.375rem] md:max-w-[95vw] md:grid-cols-2 md:rounded-lg"
                    closeButtonClassName="z-20"
                    onPointerDownOutside={(e) => {
                        if (isSubmitting) e.preventDefault();
                    }}
                    onEscapeKeyDown={(e) => {
                        if (isSubmitting) e.preventDefault();
                    }}
                >
                    <LeftPanel
                        backgroundImage={backgroundImage}
                        left={leftPanelCopy}
                    />
                    <RightPanel
                        step={step}
                        totalSteps={totalSteps}
                        currentStepKey={currentStepKey}
                        answers={answers}
                        onUpdate={updateAnswers}
                        onAdvance={() => goNextFromCurrent(answers)}
                        onAdvanceWith={goNextFromCurrent}
                        onBack={goBack}
                        onClose={close}
                        onSubmit={handleSubmit}
                        submitting={isSubmitting}
                        styleImageMap={styleImageMap}
                        errorMessage={submission.errorMessage}
                        attachmentNotice={attachmentNotice}
                        onPersistBeforeAuth={handlePersistBeforeAuth}
                        onJustRegistered={() => setJustRegistered(true)}
                        justRegistered={justRegistered}
                        confirmationEmail={accountEmail ?? answers.email}
                        isLoggedIn={userLogin.isLoggedIn}
                        matchedProject={matchedProject}
                        onAcceptMatch={handleAcceptMatch}
                        onSkipMatch={handleSkipMatch}
                        matchPending={pendingMatchAnswers !== null}
                        variantConfig={variantConfig}
                        productCategories={productCategories}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

interface LeftPanelProps {
    backgroundImage: string | null;
    left: EnquiryVariantConfig['left'];
}

const LeftPanel = ({ backgroundImage, left }: LeftPanelProps) => {
    return (
        <div className="relative hidden h-full min-h-0 flex-col overflow-hidden bg-[#1c1d1f] md:flex">
            {backgroundImage && (
                <img
                    src={backgroundImage}
                    alt=""
                    className="absolute inset-0 size-full object-cover"
                    aria-hidden
                />
            )}
            <div
                aria-hidden
                className="absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/80 to-transparent"
            />

            <APLogoWhite
                aria-hidden
                className="absolute top-[8.22%] left-[3.58%] z-10 h-auto w-[48%]"
            />

            <h2
                className={cn(
                    'relative z-10 pt-[27rem] pl-9 font-display font-bold text-white uppercase',
                    'text-[3.307rem] leading-[2.73rem] tracking-[-0.137rem]',
                )}
            >
                <span className="block">FIND YOUR</span>
                <span className="mt-[0.5rem] block md:ml-[2rem]">
                    <TextRotate
                        texts={left.rotatingWords}
                        pauseDuration={0.5}
                        rotationDuration={4}
                    />
                    {left.trailingWord && (
                        <span className="md:ml-[2rem]">
                            {left.trailingWord}
                        </span>
                    )}
                </span>
            </h2>

            <div className="relative z-10 mt-auto flex flex-col gap-3 pb-[4.625rem] pl-[1.4375rem]">
                <p className="max-w-[16.8125rem] text-[0.703rem] leading-[0.9] font-medium text-[#eee] uppercase">
                    {left.bodyCopy}
                </p>
                <div
                    aria-label="ArchiPro"
                    className="h-[0.583rem] w-18 text-white"
                >
                    <ArchiproWordmark />
                </div>
            </div>
        </div>
    );
};

const ArchiproWordmark = () => (
    <svg
        viewBox="0 0 373 49"
        fill="currentColor"
        aria-hidden
        className="size-full"
        preserveAspectRatio="xMinYMid meet"
    >
        <path d="M346.838 48.3813C331.1 48.3813 320.727 38.0031 320.727 24.2599C320.727 10.5168 331.1 0.280273 346.838 0.280273C362.576 0.280273 372.949 10.6585 372.949 24.2652C372.949 37.8718 362.502 48.3865 346.838 48.3865V48.3813ZM346.838 39.5884C357.284 39.5884 363.332 33.1998 363.332 24.2652C363.332 15.3305 357.284 9.07841 346.838 9.07841C336.391 9.07841 330.276 15.467 330.276 24.2652C330.276 33.0633 336.323 39.5884 346.838 39.5884Z" />
        <path d="M228.971 47.7409V1.28817H255.98C264.983 1.28817 271.03 6.71614 271.03 14.8948C271.03 23.0735 264.983 28.4333 255.98 28.4333H238.32V47.7409H228.971ZM238.315 9.94982V19.7769H255.838C259.549 19.7769 261.402 17.9238 261.402 14.8266C261.402 11.7294 259.549 9.94982 255.838 9.94982H238.315Z" />
        <path d="M166.507 47.7409H157.221V1.28292H166.507V19.8189H192.729V1.28292H202.083V47.7409H192.729V28.4753H166.507V47.7409Z" />
        <path d="M129.467 48.3813C113.729 48.3813 103.697 38.0031 103.697 24.2599C103.697 10.5168 113.734 0.280273 129.058 0.280273C139.368 0.280273 146.927 4.40112 151.048 11.2779L143.693 16.4329C140.465 11.6244 135.929 9.07841 129.058 9.07841C119.026 9.07841 113.251 15.467 113.251 24.2652C113.251 33.0633 119.026 39.5884 129.467 39.5884C136.475 39.5884 141.835 36.4965 144.654 32.0974L151.594 36.9742C147.399 43.8457 139.431 48.3813 129.467 48.3813Z" />
        <path d="M34.3307 1.29342L19.5482 1.28292L0.78125 47.7409H10.8655L14.3249 39.1685H39.5644L43.0238 47.7409H53.1029L34.3307 1.29342ZM17.8053 30.554L25.8633 10.6375H28.0313L36.084 30.554H17.8053Z" />
        <path d="M210.824 1.28292H220.22V47.7409H210.824V1.28292Z" />
        <path d="M90.0802 27.7666C96.1434 26.0972 99.9492 21.3832 99.9492 14.8948C99.9492 6.71614 93.9018 1.28817 84.899 1.28817H57.8904V47.7409H67.2397V28.4333H79.6705L90.8151 47.7409H101.608L90.0802 27.7666ZM67.2345 19.7769V9.94982H84.7572C88.4686 9.94982 90.3217 11.7294 90.3217 14.8266C90.3217 17.9238 88.4686 19.7769 84.7572 19.7769H67.2345Z" />
        <path d="M307.54 27.7666C313.603 26.0972 317.409 21.3832 317.409 14.8948C317.409 6.71614 311.362 1.28817 302.359 1.28817H275.35V47.7409H284.7V28.4333H297.13L308.275 47.7409H319.068L307.54 27.7666ZM284.694 19.7769V9.94982H302.217C305.928 9.94982 307.782 11.7294 307.782 14.8266C307.782 17.9238 305.928 19.7769 302.217 19.7769H284.694Z" />
    </svg>
);

interface RightPanelProps {
    step: number;
    totalSteps: number;
    currentStepKey: StepKey;
    answers: MatchAnswers;
    onUpdate: (patch: Partial<MatchAnswers>) => void;
    onAdvance: () => void;
    onAdvanceWith: (latest: MatchAnswers) => void;
    onBack: () => void;
    onClose: () => void;
    onSubmit: () => void;
    submitting: boolean;
    errorMessage: string | null;
    styleImageMap: Record<string, string>;
    attachmentNotice: string | null;
    onPersistBeforeAuth: () => void;
    onJustRegistered: () => void;
    justRegistered: boolean;
    confirmationEmail: string;
    isLoggedIn: boolean;
    matchedProject: MatchScore | null;
    onAcceptMatch: () => void;
    onSkipMatch: () => void;
    matchPending: boolean;
    variantConfig: (typeof ENQUIRY_CONFIG)[EnquiryType];
    productCategories?: ProductCategoryOption[];
}

const RightPanel = ({
    step,
    totalSteps,
    currentStepKey,
    answers,
    onUpdate,
    onAdvance,
    onAdvanceWith,
    onBack,
    onClose,
    onSubmit,
    submitting,
    errorMessage,
    styleImageMap,
    attachmentNotice,
    onPersistBeforeAuth,
    onJustRegistered,
    justRegistered,
    confirmationEmail,
    isLoggedIn,
    matchedProject,
    onAcceptMatch,
    onSkipMatch,
    matchPending,
    variantConfig,
    productCategories,
}: RightPanelProps) => {
    const isDone = currentStepKey === 'done';

    return (
        <div className="relative flex h-full min-h-0 flex-col bg-[#eaeaea]">
            {matchPending && (
                <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#eaeaea]/85 backdrop-blur-sm">
                    <p className="text-[0.875rem] font-medium tracking-[-0.01em] text-[#1c1d1f]">
                        Checking your projects…
                    </p>
                </div>
            )}
            <Stepper step={step} totalSteps={totalSteps} done={isDone} />
            <div className="scrollbar-elegant min-h-0 flex-1 overflow-y-auto px-9 pt-[3.5rem] pb-32">
                <StepBody
                    currentStepKey={currentStepKey}
                    answers={answers}
                    onUpdate={onUpdate}
                    onAdvance={onAdvance}
                    onAdvanceWith={onAdvanceWith}
                    onBack={onBack}
                    onClose={onClose}
                    onSubmit={onSubmit}
                    submitting={submitting}
                    errorMessage={errorMessage}
                    styleImageMap={styleImageMap}
                    attachmentNotice={attachmentNotice}
                    onPersistBeforeAuth={onPersistBeforeAuth}
                    onJustRegistered={onJustRegistered}
                    justRegistered={justRegistered}
                    confirmationEmail={confirmationEmail}
                    isLoggedIn={isLoggedIn}
                    matchedProject={matchedProject}
                    onAcceptMatch={onAcceptMatch}
                    onSkipMatch={onSkipMatch}
                    variantConfig={variantConfig}
                    productCategories={productCategories}
                />
            </div>
            <FooterActions
                currentStepKey={currentStepKey}
                isFirstStep={step === 1}
                showRegionContinue={variantConfig.step2.multiSelect}
                regionContinueDisabled={
                    !answers.region || answers.region.trim() === ''
                }
                onBack={onBack}
                onCancel={onClose}
                onContinue={onAdvance}
                submitting={submitting}
            />
        </div>
    );
};

interface StepBodyProps {
    currentStepKey: StepKey;
    answers: MatchAnswers;
    onUpdate: (patch: Partial<MatchAnswers>) => void;
    onAdvance: () => void;
    onAdvanceWith: (latest: MatchAnswers) => void;
    onBack: () => void;
    onClose: () => void;
    onSubmit: () => void;
    submitting: boolean;
    errorMessage: string | null;
    styleImageMap: Record<string, string>;
    attachmentNotice: string | null;
    onPersistBeforeAuth: () => void;
    onJustRegistered: () => void;
    justRegistered: boolean;
    confirmationEmail: string;
    isLoggedIn: boolean;
    matchedProject: MatchScore | null;
    onAcceptMatch: () => void;
    onSkipMatch: () => void;
    variantConfig: (typeof ENQUIRY_CONFIG)[EnquiryType];
    productCategories?: ProductCategoryOption[];
}

const StepBody = ({
    currentStepKey,
    answers,
    onUpdate,
    onAdvance,
    onAdvanceWith,
    onBack,
    onClose,
    onSubmit,
    submitting,
    errorMessage,
    styleImageMap,
    attachmentNotice,
    onPersistBeforeAuth,
    onJustRegistered,
    justRegistered,
    confirmationEmail,
    isLoggedIn,
    matchedProject,
    onAcceptMatch,
    onSkipMatch,
    variantConfig,
    productCategories,
}: StepBodyProps) => {
    const selectAndAdvance = (patch: Partial<MatchAnswers>) => {
        // Switching enquiry type must blank variant-conditional answers so a
        // re-pick from the selector step can't leak (e.g.) a stale
        // "Architects" ProfessionalType into a product brief.
        const fullPatch: Partial<MatchAnswers> =
            patch.enquiryType && patch.enquiryType !== answers.enquiryType
                ? {
                      ...patch,
                      projectScope: null,
                      productCategory: null,
                      style: null,
                      professionals: null,
                      buildHubID: null,
                  }
                : patch;
        onUpdate(fullPatch);
        onAdvanceWith({ ...answers, ...fullPatch });
    };

    switch (currentStepKey) {
        case 'enquiryType':
            return (
                <Step0EnquiryType
                    value={answers.enquiryType}
                    onSelect={(enquiryType) =>
                        selectAndAdvance({ enquiryType })
                    }
                />
            );
        case 'userType':
            return (
                <Step1UserType
                    title={variantConfig.step1.title}
                    options={variantConfig.step1.options}
                    value={answers.userType}
                    onSelect={(userType) => selectAndAdvance({ userType })}
                />
            );
        case 'region':
            return (
                <Step2Location
                    title={variantConfig.step2.title}
                    helperText={variantConfig.step2.helperText}
                    multiSelect={variantConfig.step2.multiSelect}
                    value={answers.region}
                    // Multi-select updates the region in place without
                    // auto-advancing — the Continue button in FooterActions
                    // moves the flow on once the user is done picking.
                    onSelect={(region) =>
                        variantConfig.step2.multiSelect
                            ? onUpdate({ region })
                            : selectAndAdvance({ region })
                    }
                />
            );
        case 'projectType':
            return (
                <Step3ProjectType
                    title={variantConfig.step3.title}
                    options={variantConfig.step3.options}
                    value={answers.projectType}
                    onSelect={(projectType) =>
                        selectAndAdvance({ projectType })
                    }
                />
            );
        case 'projectScope':
            return (
                <Step4ProjectScope
                    value={answers.projectScope}
                    onSelect={(projectScope) =>
                        selectAndAdvance({ projectScope })
                    }
                />
            );
        case 'matchProject':
            return matchedProject ? (
                <StepMatchProject
                    match={matchedProject}
                    onAccept={onAcceptMatch}
                    onSkip={onSkipMatch}
                />
            ) : null;
        case 'productCategory': {
            const cfg = variantConfig.stepProductCategory;
            if (!cfg) return null;
            // Live options from the consumer (e.g. /products loader) take
            // precedence; fallback list keeps the modal usable when the
            // consumer hasn't wired the loader.
            const options: ProductCategoryOption[] =
                productCategories && productCategories.length > 0
                    ? productCategories
                    : cfg.fallbackOptions.map((label) => ({
                          value: label,
                          label,
                      }));
            return (
                <Step4ProductCategory
                    title={cfg.title}
                    options={options}
                    value={answers.productCategory}
                    onSelect={(productCategory) =>
                        selectAndAdvance({ productCategory })
                    }
                />
            );
        }
        case 'style':
            return (
                <Step4Style
                    value={answers.style}
                    onSelect={(style) => selectAndAdvance({ style })}
                    images={styleImageMap}
                />
            );
        case 'budget':
            return (
                <Step5Budget
                    title={variantConfig.step5Budget.title}
                    helperText={variantConfig.step5Budget.helperText}
                    options={variantConfig.step5Budget.options}
                    value={answers.budget}
                    onSelect={(budget) => selectAndAdvance({ budget })}
                />
            );
        case 'details':
            return (
                <Step6Project
                    title={variantConfig.step6.title}
                    lookingForField={variantConfig.step6.lookingForField}
                    showTimeline={variantConfig.step6.showTimeline}
                    descriptionLabel={variantConfig.step6.descriptionLabel}
                    descriptionPlaceholder={
                        variantConfig.step6.descriptionPlaceholder
                    }
                    timelineLabel={variantConfig.step6.timelineLabel}
                    timelineStartLabel={variantConfig.step6.timelineStartLabel}
                    timelineEndLabel={variantConfig.step6.timelineEndLabel}
                    value={{
                        description: answers.description,
                        professionals: answers.professionals,
                        startDate: answers.startDate,
                        endDate: answers.endDate,
                        attachments: answers.attachments,
                    }}
                    onChange={(next) => onUpdate(next)}
                    attachmentNotice={attachmentNotice ?? undefined}
                />
            );
        case 'account':
            return (
                <Step7Account
                    email={answers.email}
                    onEmailChange={(email) => onUpdate({ email })}
                    onContinue={onSubmit}
                    onBack={onBack}
                    submitting={submitting}
                    errorMessage={errorMessage}
                    onPersistBeforeAuth={onPersistBeforeAuth}
                    onJustRegistered={onJustRegistered}
                    justRegistered={justRegistered}
                    loggedInConfirmationPrefix={
                        variantConfig.step7.loggedInConfirmationPrefix
                    }
                    loggedInConfirmationSuffix={
                        variantConfig.step7.loggedInConfirmationSuffix
                    }
                    anonymousBodyCopy={variantConfig.step7.anonymousBodyCopy}
                />
            );
        case 'done':
            return (
                <Step8Done
                    onClose={onClose}
                    email={confirmationEmail}
                    isLoggedIn={isLoggedIn}
                    matchSubjectPlural={variantConfig.step8.matchSubjectPlural}
                />
            );
        default:
            return null;
    }
};

interface FooterActionsProps {
    currentStepKey: StepKey;
    isFirstStep: boolean;
    /** True when the active step is region AND the variant has multiSelect mode on. */
    showRegionContinue: boolean;
    /** Disable Continue on region step until at least one region is picked. */
    regionContinueDisabled: boolean;
    onBack: () => void;
    onCancel: () => void;
    onContinue: () => void;
    submitting: boolean;
}

const FooterActions = ({
    currentStepKey,
    isFirstStep,
    showRegionContinue,
    regionContinueDisabled,
    onBack,
    onCancel,
    onContinue,
    submitting,
}: FooterActionsProps) => {
    // These steps render their own CTAs.
    if (
        currentStepKey === 'account' ||
        currentStepKey === 'done' ||
        currentStepKey === 'matchProject'
    ) {
        return null;
    }

    const showContinue =
        currentStepKey === 'details' ||
        (currentStepKey === 'region' && showRegionContinue);
    const showBack = !isFirstStep;
    const showCancel = isFirstStep;
    const continueDisabled =
        submitting ||
        (currentStepKey === 'region' &&
            showRegionContinue &&
            regionContinueDisabled);

    return (
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-3 pt-4 pb-7">
            {showCancel && (
                <OutlineButton onClick={onCancel} disabled={submitting}>
                    Cancel
                </OutlineButton>
            )}
            {showBack && (
                <OutlineButton onClick={onBack} disabled={submitting}>
                    Back
                </OutlineButton>
            )}
            {showContinue && (
                <FilledButton onClick={onContinue} disabled={continueDisabled}>
                    Continue
                </FilledButton>
            )}
        </div>
    );
};

const OutlineButton = ({
    onClick,
    disabled,
    children,
}: {
    onClick: () => void;
    disabled?: boolean;
    children: React.ReactNode;
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="inline-flex h-[2.85rem] items-center justify-center rounded-full border border-black bg-transparent px-7 text-[1rem] font-medium tracking-[0.02em] text-[#1c1d1f] transition-colors hover:bg-black hover:text-white disabled:opacity-60"
    >
        {children}
    </button>
);

const FilledButton = ({
    onClick,
    disabled,
    children,
}: {
    onClick: () => void;
    disabled?: boolean;
    children: React.ReactNode;
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="inline-flex h-[2.85rem] items-center justify-center rounded-full bg-[#323232] px-9 text-[1rem] font-medium tracking-[0.02em] text-white shadow-md transition-colors hover:bg-black disabled:opacity-60"
    >
        {children}
    </button>
);

interface StepperProps {
    step: number;
    totalSteps: number;
    done?: boolean;
}

const Stepper = ({ step, totalSteps, done }: StepperProps) => {
    return (
        <div className="flex items-center gap-3 px-9 pt-7">
            <span className="shrink-0 text-[0.75rem] tracking-[-0.02em] text-[#313131]">
                {done ? 'Done!' : `Step ${step}/${totalSteps}`}
            </span>
            <div className="flex flex-1 items-center gap-1">
                {Array.from({ length: totalSteps }).map((_, i) => (
                    <span
                        key={i}
                        className={cn(
                            'h-[0.188rem] flex-1',
                            done || i < step
                                ? 'bg-[#030201]'
                                : 'bg-[#8d8d8d] opacity-30',
                        )}
                    />
                ))}
            </div>
        </div>
    );
};
